import React, { lazy, Suspense, useEffect, useState } from 'react'
import ErrorBoundary from '@fs/zion-error-boundary'
import { geoData } from '@fs/zion-config'
import { NoticeLoading } from '@fs/zion-icon'
import { i18n } from '@fs/zion-locale'
import { AuthRoute, Route, NotFound, Switch, useParams } from '@fs/zion-router'
import Root from '@fs/zion-root'
import { AppLayout, colors, Row } from '@fs/zion-ui'

import useIsAdmin from './services/useIsAdmin'
import SitesUsabilla from './components/SitesUsabilla'

const CountryPage = lazy(() => import('./components/directory/CountryPage'))
const AdminSitesListPage = lazy(() => import('./components/admin/AdminSitesListPage'))
const AdminSitePage = lazy(() => import('./components/admin/AdminSitePage'))
const BannersPage = lazy(() => import('./components/directory/BannersPage'))
const Unauthorized = lazy(() => import('./components/admin/Unauthorized'))
const HomePage = lazy(() => import('./components/directory/HomePage'))
const SitesPage = lazy(() => import('./components/sites/SitesPage'))
const SitePersonsPage = lazy(() => import('./components/site-persons/SitePersonsPage'))
const PersonSelectedProvider = lazy(() => import('./components/PersonSelectedProvider'))
const LimitedSupportBanner = lazy(() => import('./components/LimitedSupportBanner'))
const LimitedSupportProvider = lazy(() => import('./components/LimitedSupportProvider'))
const MapMarkers = lazy(() => import('./components/maps/icons/MapMarkers'))

const rootMatchPath = '/([a-z]{2}|zh-hans|ceb)?/(cemeteries|historic-sites|memorials|monuments|eurona)?/(travel)?'
const cemeteriesMatchPath = window.location.hostname === 'localhost' ? '(/[a-z]{2}|zh-hans|ceb)?(/cemeteries)?' : ''

// Each of the following languages must be a string containing a 2-char
// ISO language code (all lower case) followed by a hyphen and a 2-char
// ISO country code (all upper case) and must correspond to a folder in
// src/locales containing a translation.json. For example, a language of
// "pt-PT" (the Portuguese language in Portugal) must have a file at
// src/locales/pt-PT/translation.json.
const SUPPORTED_EXTENDED_LANGUAGES = ['pt-PT']

/**
 * If the selected language for this app does not have a country code, this will
 * look for a supported extended language that matches the user's country or the
 * country in the browser language setting. This function must be invoked in a
 * rendered component like App() to ensure that the i18n object is initialized.
 * Translations from the base language (without the country code) that are not
 * found in the extended language are added to the extended language.
 */
function handleExtendedLanguage() {
  if (!i18n.language.includes('-')) {
    const languageCode = i18n.language
    const browserLanguageParts = window?.navigator?.language?.split('-') || []
    const browserCountryCode = browserLanguageParts.length === 2 ? browserLanguageParts[1] : ''
    const userCountryCode = geoData?.country || ''
    let language = ''
    if (SUPPORTED_EXTENDED_LANGUAGES.includes(`${i18n.language}-${userCountryCode}`)) {
      language = `${languageCode}-${userCountryCode}`
    } else if (SUPPORTED_EXTENDED_LANGUAGES.includes(`${languageCode}-${browserCountryCode}`)) {
      language = `${languageCode}-${browserCountryCode}`
    }
    if (language) {
      if (!i18n.options.supportedLngs.includes(language)) {
        i18n.options.supportedLngs.push(language)
      }
      i18n.options.lowerCaseLng = false
      i18n.changeLanguage(language).then(() => {
        const resource = i18n.getResourceBundle(languageCode, 'translation')
        // This does not overwrite the existing language resource, but adds to it.
        i18n.addResourceBundle(language, 'translation', resource, true, false)
      })
    }
  }
}

function App() {
  handleExtendedLanguage()
  const bgColor = window?.location?.href?.includes('/locality') ? colors.gray03 : colors.gray00
  const hasHidehfParam = window.location.search.includes('hidehf')
  const hasHidehfLocalStorage = window?.localStorage?.getItem('hidehf') === 'true'
  const hidehf = hasHidehfParam || hasHidehfLocalStorage

  if (hidehf) {
    window?.localStorage?.setItem('hidehf', 'true')
  }

  return (
    <Root
      analytics
      footer={{
        useFatFooter: window?.location?.pathname.includes('/cemeteries') && !window.location.href.includes('/sites'),
      }}
      header={{ hidden: hidehf }}
    >
      <AppLayout fullWidth backgroundColor={bgColor}>
        <ErrorBoundary>
          <Switch>
            <Route path={`${rootMatchPath}/admin`} component={TravelAdminApp} requirePersonalData />
            <Route path={`${rootMatchPath}`} component={TravelApp} requirePersonalData />
            <Route component={NotFound} requirePersonalData />
          </Switch>
        </ErrorBoundary>
      </AppLayout>
    </Root>
  )
}

function TravelApp() {
  const hasCemeteriesInPath = window?.location?.pathname.includes('/cemeteries')
  return (
    <Suspense>
      <PersonSelectedProvider>
        <LimitedSupportProvider>
          <LimitedSupportBanner />
          <Switch>
            {/* /banners and /markers are for developer use only */}
            <Route exact path={`${rootMatchPath}/banners`} component={BannersPage} requirePersonalData />
            <Route exact path={`${rootMatchPath}/markers`} component={MapMarkers} requirePersonalData />

            {window.location.hostname === 'localhost' && (
              <Route exact path={rootMatchPath} component={HomePage} requirePersonalData />
            )}
            <Route exact path={`${rootMatchPath}/sites`} component={SitesPage} requirePersonalData />
            <Route
              exact
              path={`${rootMatchPath}/sites/:siteId/:siteName`}
              component={SitePersonsPage}
              requirePersonalData
            />
            <Route
              exact
              path={`${rootMatchPath}/sites/:siteId/:siteName/person/:personId`}
              component={SitePersonsPageRedirect}
              requirePersonalData
            />

            {hasCemeteriesInPath && (
              <Route exact path={`${cemeteriesMatchPath}/`} component={HomePage} requirePersonalData />
            )}

            <Route exact path={`${cemeteriesMatchPath}/:countryUrlParam`} component={CountryPage} requirePersonalData />

            <Route component={NotFound} requirePersonalData />
          </Switch>
        </LimitedSupportProvider>
      </PersonSelectedProvider>
      <SitesUsabilla />
    </Suspense>
  )
}

function SitePersonsPageRedirect() {
  const { personId, personName } = useParams()

  if (personId && personName) {
    const params = new URLSearchParams(window.location.search)
    params.set('searchText', personName)
    window.location.href = `${window.location.href
      .split('?')[0]
      .replace(`/person/${personId}`, '')}?${params.toString()}`
  }

  return !personId && <Route component={NotFound} />
}

function TravelAdminApp() {
  const { loading, data, error } = useIsAdmin()
  const [authorized, setAuthorized] = useState(false)
  useEffect(() => {
    if (!loading && !error && data?.authorized) {
      setAuthorized(true)
    }
  }, [loading, error, data])
  return (
    <Suspense>
      {loading && (
        <Row alignX="center">
          <NoticeLoading size="lg" />
        </Row>
      )}
      {!loading && authorized && (
        <Switch>
          <AuthRoute exact path={`${rootMatchPath}/admin/sites`} component={AdminSitesListPage} />
          <AuthRoute exact path={`${rootMatchPath}/admin/sites/:siteId/:step?`} component={AdminSitePage} />
          <Route component={NotFound} />
        </Switch>
      )}
      {!loading && !authorized && (
        <Switch>
          <AuthRoute component={Unauthorized} />
        </Switch>
      )}
    </Suspense>
  )
}

export default App
